import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MatSort} from '@angular/material/sort';
import {SelectionModel} from '@angular/cdk/collections';
import {MatTableDataSource} from '@angular/material/table';

import {Vehicle} from "@commons/models/domain/Vehicle";

import {LinkViewModel} from "@data-sharing/view-models/link-view-model";
import {
  ConfirmLinksRemovalModalComponent
} from "@data-sharing/routes/links-list/components/confirm-links-removal-modal/confirm-links-removal-modal.component";

import {DialogService} from "@shared/services/dialog.service";
import {MatTableHelperService} from "@shared/modules/material/mat-table-helper.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'dsm-links-list-table',
  templateUrl: './links-list-table.component.html',
  styleUrls: ['./links-list-table.component.less']
})
export class LinksListTableComponent implements OnInit, OnChanges {

  @Input() isLoading = false;
  @Input() vehicles: Vehicle[] = [];
  @Input() search = '';

  @Input() rows: LinkViewModel[] = [];

  @Output() viewLink = new EventEmitter<LinkViewModel>();
  @Output() reloadList = new EventEmitter();

  @ViewChild(MatSort) set setMatSort(matSort: MatSort) {
    if (matSort) {
      this.matSort = matSort;
      this.dataSource.sort = this.matSort;
    }
  }

  private matSort?: MatSort;
  readonly showVehiclesCount = 2;

  selectedRow?: LinkViewModel;
  displayedColumns: string[] = ['select', 'name', 'endDate', 'vehicles', 'url'];
  selectionModel = new SelectionModel<LinkViewModel>(true, []);
  dataSource: MatTableDataSource<LinkViewModel> = new MatTableDataSource<LinkViewModel>([]);
  activeTranslation = '';
  inactiveTranslation = '';

  get isRemoveActive(): boolean {
    return this.selectionModel.selected.length > 0;
  }

  get isAllSelected(): boolean {
    const filter = this.search ? this.search : '';
    const selectedLength = this.selectionModel.selected.filter(x => this.filterPredicate(x, filter)).length;
    const allItemsLength = this.search ? this.dataSource.filteredData.length : this.dataSource.data.length;

    return this.matTableHelperService.isAllSelected(selectedLength, allItemsLength);
  }

  get isAllChecked(): boolean {
    const result = this.selectionModel.hasValue() && this.isAllSelected;
    return result;
  }

  get isIndeterminateChecked(): boolean {
    const result = this.selectionModel.hasValue() &&
      this.dataSource.filteredData.some(x => this.selectionModel.isSelected(x)) &&
      !this.isAllSelected;
    return result;
  }

  constructor(
    private dialogService: DialogService,
    private matTableHelperService: MatTableHelperService,
    private translateService: TranslateService,
  ) {
    this.setTranslations();
    this.dataSource.filterPredicate = this.filterPredicate;
    this.dataSource.sortData = this.sortData();
  }

  private setTranslations() {
    this.translateService.get('labels.active').subscribe(x => this.activeTranslation = x);
    this.translateService.get('labels.inactive').subscribe(x => this.inactiveTranslation = x);
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.rows) {
      this.dataSource.data = this.rows;
      this.setLinksVehicles();
    }

    if (this.vehicles && this.vehicles.length > 0 && this.rows.length === 0)
      this.setLinksVehicles();

    if (this.search || this.search === '')
      this.dataSource.filter = this.search.trim().toLowerCase();
  }

  onRowSelected(linkViewModel: LinkViewModel) {
    this.selectedRow = linkViewModel;
    this.viewLink.emit(this.selectedRow);
  }

  onRemove() {
    if (!this.isRemoveActive)
      return;

    const dialogRef = this.dialogService.open(ConfirmLinksRemovalModalComponent, this.selectionModel.selected);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.selectionModel.clear();
        this.reloadList.emit();
      }
    });
  }

  onSelectAll() {
    if (this.isAllSelected) {
      this.selectionModel.clear();
      return;
    }
    this.selectionModel.select(...this.dataSource.filteredData);
  }

  onOpenLink(model: LinkViewModel) {
    window.open(model.url, '_blank')!.focus();
  }

  private setLinksVehicles() {
    for (let model of this.rows) {
      const vehicles = this.vehicles.filter(v => model.link.vehicles.some(lv => lv === v.Id));
      model.fullVehicles = vehicles;
    }
  }

  private sortData() {
    let sortFunction =
      (items: LinkViewModel[], sort: MatSort): LinkViewModel[] => {
        if (!sort.active || sort.direction === '') {
          return items;
        }
        return items.sort((a: LinkViewModel, b: LinkViewModel) => {
          let comparatorResult = 0;
          switch (sort.active) {
            case 'name':
              comparatorResult = a.link.name.localeCompare(b.name);
              break;

            case 'endDate':
              return this.sortEndDate(a, b);
          }
          return comparatorResult * (sort.direction == 'asc' ? 1 : -1);
        });
      };
    return sortFunction;
  }

  private sortEndDate(a: LinkViewModel, b: LinkViewModel): number {
    if (a.link?.endDate == undefined)
      return 1;

    if (b.link?.endDate == undefined)
      return -1;

    const a1 = new Date(a.link?.endDate).getTime();
    const b1 = new Date(b.link?.endDate).getTime();
    const result = a1 - b1;
    // console.warn( 'result='+result + ' a=' + a.link.endDate + ' b=' + b.link.endDate);
    return result;
  }

  private filterPredicate(data: LinkViewModel, filter: string): boolean {
    filter = filter.toLowerCase();
    const result =
      data.link.name.toLowerCase().includes(filter) ||
      data.url.toLowerCase().includes(filter) ||
      data.fullVehicles.some(x => x.Nm.toLowerCase().includes(filter));
    return result;
  }
}
